import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { FaBars, FaTimes, FaWhatsapp, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/hybrid', label: t('nav.hybrid') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/why-choose-us', label: t('nav.why') },
    { path: '/reviews', label: t('nav.reviews') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 shadow-sm" data-testid="main-header">
      {/* Top Utility Bar */}
      <div className="bg-dark-navy text-white text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-10">
            <div className="flex items-center space-x-8">
              <a href="tel:+6069513317" className="flex items-center space-x-2 hover:text-blue-300 transition-colors" data-testid="topbar-phone">
                <FaPhone size={12} />
                <span>06-951 3317</span>
              </a>
              <div className="flex items-center space-x-2">
                <FaMapMarkerAlt size={12} />
                <span>Lot 3663, Batu 1 3/4 Jalan Bakri 84000 Muar Johor</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => language !== 'en' && toggleLanguage()}
                data-testid="lang-en"
                className={`text-sm font-medium transition-colors ${language === 'en' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
              >
                EN
              </button>
              <span className="text-gray-500">|</span>
              <button
                onClick={() => language !== 'zh' && toggleLanguage()}
                data-testid="lang-zh"
                className={`text-sm font-medium transition-colors ${language === 'zh' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
              >
                中文
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-28">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-4 flex-shrink-0" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/9y8bohg9_LK.jpg" 
                alt="Leng Kee Auto Service Centre Logo"
                className="w-20 h-20 object-contain"
              />
              <div className="hidden sm:block">
                <div className="font-cabinet font-extrabold text-dark-navy text-sm lg:text-base leading-tight max-w-[320px]">
                  LENG KEE AUTO SERVICE<br/>CENTRE SDN BHD
                </div>
                <div className="text-sm text-gray-700 mt-1 font-medium">
                  龙记汽车服务中心有限公司
                </div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center space-x-5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                  className={`font-cabinet text-xs font-semibold tracking-wider hover:text-royal-blue transition-colors uppercase whitespace-nowrap ${
                    isActive(link.path) ? 'text-royal-blue border-b-2 border-royal-blue pb-1' : 'text-dark-navy'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/message/EFBOOUUK2AO2K1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="hidden md:flex items-center space-x-2 px-5 py-2.5 border-2 border-royal-blue text-royal-blue font-cabinet font-bold text-sm hover:bg-royal-blue hover:text-white transition-colors rounded"
            >
              <FaWhatsapp size={18} />
              <span>WHATSAPP US</span>
            </a>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
              className="xl:hidden p-2 text-dark-navy hover:bg-gray-100 rounded transition-colors"
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="xl:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
              <nav className="flex flex-col space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`font-cabinet text-sm font-semibold tracking-wide px-4 py-3 hover:bg-gray-50 transition-colors uppercase ${
                      isActive(link.path) ? 'text-royal-blue bg-gray-50' : 'text-dark-navy'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="flex items-center justify-between px-4 pt-4 border-t border-gray-100 mt-2">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={toggleLanguage}
                      className="flex items-center space-x-2 px-3 py-2 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-sm font-medium text-dark-navy">{language === 'en' ? 'EN' : '中文'}</span>
                    </button>
                  </div>
                  <a
                    href="https://wa.me/message/EFBOOUUK2AO2K1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-whatsapp text-white font-cabinet font-bold text-sm hover:bg-[#1EBE5A] transition-colors"
                  >
                    <FaWhatsapp size={16} />
                    <span>WHATSAPP</span>
                  </a>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
